import { Alert, Checkbox, HStack, Icon, Input, InputGroup, Skeleton, Stack, Switch, Table } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { FaSearch } from 'react-icons/fa';

import type { IndicatorYearEntityStatus, IndicatorYearSettingUpdateEntity } from '@/client';
import { IndicatorsService } from '@/client/sdk.gen.ts';
import { Route } from '@/routes/_layout/$slug/indicators/collecte/$year/manage.tsx';

import { PartialIndicatorYearSetting } from './ManageCollecteUpdateDrawer.tsx';

function getIndicatorYearSettingEntitiesQueryOptions({
  slug,
  year,
  indicatorIds,
}: {
  slug: string;
  year: number;
  indicatorIds: string[];
}) {
  return {
    queryKey: ['indicatorYearSettingEntities', { slug, year, indicatorIds }],
    queryFn: async () => {
      return await IndicatorsService.readIndicatorYearSettingsEntities({
        companySlug: slug,
        year: year,
        requestBody: indicatorIds,
      });
    },
  };
}

export const ManageCollecteUpdateDrawerEntitiesTable = ({
  selectedIndicatorYearSettings = [],
  onChange,
}: {
  selectedIndicatorYearSettings: PartialIndicatorYearSetting[];
  onChange: (e: Partial<IndicatorYearSettingUpdateEntity>[]) => void;
}) => {
  const { slug, year } = Route.useParams();

  const { data: entities, isLoading } = useQuery({
    ...getIndicatorYearSettingEntitiesQueryOptions({
      slug,
      year: year,
      indicatorIds: selectedIndicatorYearSettings.map((item) => item.indicator_id).filter((item) => item != null),
    }),
  });
  const [collectedByEntities, setCollectedByEntities] = useState(false);

  const defaultValues = useMemo(() => {
    if (year === '') return [];
    if (entities?.data.some((item) => item.activated === true)) setCollectedByEntities(true);
    else setCollectedByEntities(false);
    return (entities?.data ?? []).map((item) => ({
      entity_id: item.entity_id,
      activated: item.activated,
      name: item.name,
      objective: item.objective,
    })) as IndicatorYearEntityStatus[];
  }, [entities, year]);

  const [searchValue, setSearchValue] = useState<string>('');
  const [editedValues, setEditedValues] = useState(defaultValues);

  useEffect(() => {
    setEditedValues(defaultValues);
  }, [defaultValues]);

  const entitiesSelected = useMemo(
    () => editedValues.filter((item) => item.activated === true).map((item) => item.entity_id),
    [editedValues]
  );

  const compareEntityArrays = (
    arr1: IndicatorYearSettingUpdateEntity[],
    arr2: IndicatorYearSettingUpdateEntity[]
  ): Partial<IndicatorYearSettingUpdateEntity>[] => {
    const result: IndicatorYearSettingUpdateEntity[] = [];
    const map2 = new Map(arr2.map((obj) => [obj.entity_id, obj]));

    for (const { entity_id, activated, objective } of arr1) {
      const obj2 = map2.get(entity_id);
      if (!obj2) continue;

      const { activated: activated2, objective: objective2 } = obj2;
      let diffObj: IndicatorYearSettingUpdateEntity | null = null;

      if (activated !== activated2 || objective !== objective2) {
        diffObj = { entity_id };
        if (activated !== activated2) diffObj.activated = activated2;
        if (objective !== objective2) diffObj.objective = objective2;
        result.push(diffObj);
      }
    }
    return result;
  };

  const handleEntityChange = (entityId: string | null, updatedEntity: Partial<IndicatorYearSettingUpdateEntity>) => {
    const newEditedValues = editedValues.map((entity) =>
      (entityId === null ? entity : entity.entity_id === entityId) ? { ...entity, ...updatedEntity } : entity
    );
    setEditedValues(newEditedValues);
    onChange(compareEntityArrays(defaultValues, newEditedValues));
  };

  const hasSelection = entitiesSelected.length > 0;
  const indeterminate =
    (hasSelection && entitiesSelected.length < defaultValues.length) ||
    editedValues.some((item) => item.activated !== editedValues[0].activated || item.activated === null);

  if (isLoading) {
    return (
      <HStack gap="5">
        <Stack flex="1">
          <Skeleton height="5" />
          <Skeleton height="5" />
          <Skeleton height="5" />
        </Stack>
      </HStack>
    );
  }

  const filtredValues = editedValues.filter((item) =>
    searchValue
      ? Object.values(item).some((val) => val?.toString().toLowerCase().includes(searchValue.toLowerCase()))
      : true
  );

  return (
    <>
      <HStack as="label" fontWeight={'medium'} fontSize={'sm'} lineHeight={'1.25rem'}>
        {t('collection.manage.columnsHeader.entityConfiguration')}
        <Switch.Root
          checked={collectedByEntities}
          onCheckedChange={(e) => {
            setCollectedByEntities(e.checked);
            if (!e.checked) {
              handleEntityChange(null, {
                activated: !e.checked,
              });
            }
          }}
        >
          <Switch.HiddenInput />
          <Switch.Control />
        </Switch.Root>
      </HStack>
      {collectedByEntities && (
        <>
          {selectedIndicatorYearSettings.length !== 1 && (
            <Alert.Root status="info">
              <Alert.Indicator />
              <Alert.Title>{t('messages.hints.mulitpleIndicatorsSelected')}</Alert.Title>
            </Alert.Root>
          )}
          <InputGroup
            w="250px"
            ml={'auto'}
            placeSelf={'end'}
            endElement={
              <Icon cursor={'pointer'} _hover={{ color: 'primary' }}>
                <FaSearch />
              </Icon>
            }
          >
            <Input
              variant="flushed"
              size="sm"
              placeholder={t('ui.search')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              autoFocus
            />
          </InputGroup>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader w="6">
                  <Checkbox.Root
                    size="sm"
                    top="0.5"
                    checked={indeterminate ? 'indeterminate' : hasSelection}
                    onCheckedChange={(e) => {
                      handleEntityChange(null, {
                        activated: !!e.checked,
                      });
                    }}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                  </Checkbox.Root>
                </Table.ColumnHeader>
                <Table.ColumnHeader pr={0}>{t('collection.manage.columnsHeader.entityName')}</Table.ColumnHeader>
                {selectedIndicatorYearSettings.length === 1 && (
                  <Table.ColumnHeader>{t('collection.manage.columnsHeader.objective')}</Table.ColumnHeader>
                )}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filtredValues.map((item) => (
                <Table.Row key={item.entity_id}>
                  <Table.Cell
                    onClick={(e) => {
                      e.preventDefault();
                      handleEntityChange(item.entity_id, {
                        activated: !item.activated,
                      });
                    }}
                  >
                    <Checkbox.Root
                      size="sm"
                      top="0.5"
                      checked={item.activated === null ? 'indeterminate' : item.activated}
                    >
                      <Checkbox.Control />
                    </Checkbox.Root>
                  </Table.Cell>
                  <Table.Cell>{item.name}</Table.Cell>
                  {selectedIndicatorYearSettings.length === 1 && (
                    <Table.Cell>
                      <InputGroup
                        endElement={selectedIndicatorYearSettings[0].unit ? selectedIndicatorYearSettings[0].unit : ''}
                      >
                        <Input
                          disabled={!item.activated}
                          placeholder="0,00"
                          type="number"
                          value={item.activated ? (item.objective?.toString() ?? '') : []}
                          min={0}
                          onChange={(e) => {
                            handleEntityChange(item.entity_id, {
                              objective: Number.parseFloat(e.target.value),
                            });
                          }}
                        />
                      </InputGroup>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </>
      )}
    </>
  );
};

export default ManageCollecteUpdateDrawerEntitiesTable;
